import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { TxFormGroup } from '../FormGroup';
import { TxInput } from '../../components/Input';
import { TxAutocomplete } from '../../components/Autocomplete';
import { TxInputMask } from '../../components/InputMask';
import { WhatsThisModal } from '../WhatsThisModal/WhatsThisModal';
import { getAcceptedCardsString, lookupErrorMessage, getFieldLabels, stateNames, getStateDisplayValue, } from '../../utils';
import { CaretIcon } from '../../icons/CaretIcon';
import { paymentFormSchema } from '../../lib/validation/paymentForm';
import { useFieldMapping } from '../../hooks/useFieldMapping';
import { PAYMENT_CARD_FIELD_MAPPING, PAYMENT_FORM_VALUE_CHECK_FIELDS, } from '../../constants/fieldMappings';
import { usePaymentCardFormFilled } from '../../hooks/usePaymentCardFormFilled';
export var TxPaymentCardFormMode;
(function (TxPaymentCardFormMode) {
    TxPaymentCardFormMode["AccountSettingsAdd"] = "AccountSettingsAdd";
    TxPaymentCardFormMode["TransactionAdd"] = "TransactionAdd";
    TxPaymentCardFormMode["Edit"] = "Edit";
})(TxPaymentCardFormMode || (TxPaymentCardFormMode = {}));
const defaultValues = {
    name: '',
    streetAddress: '',
    streetAddress2: '',
    cardNumber: '',
    cvv: undefined,
    city: '',
    state: '',
    zip: '',
    expirationDate: '',
    saveCard: false,
    cardType: '',
    country: '',
    postalCode: '',
};
export const TxPaymentCardForm = ({ acceptedCards = ['VISA', 'MasterCard', 'Discover', 'American Express'], className, paymentMethodTitle, paymentMethodSubTitle, billingAddressTitle, billingAddressSubTitle, iFrameUrl, mode = TxPaymentCardFormMode.Edit, onCardFieldMessage = () => { }, onFieldChange = () => { }, onFieldBlur = () => { }, onFormError = () => { }, onFormFilled = () => { }, spanish = false, errorMessages = {}, values = {}, }) => {
    const [isCardModalOpen, setIsCardModalOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [cardData, setCardData] = useState({});
    const isAmexAccepted = useMemo(() => acceptedCards.includes('American Express'), [acceptedCards]);
    const labels = useMemo(() => getFieldLabels(spanish), [spanish]);
    // Use centralized field mapping
    const fieldMapping = PAYMENT_CARD_FIELD_MAPPING;
    useEffect(() => {
        const handleMessage = (event) => {
            if (!event || typeof event.data !== 'string') {
                return;
            }
            if (!event.origin.includes('snappaydirect') && !event.origin.includes('localhost')) {
                return;
            }
            if (event.data.includes('message')) {
                const data = JSON.parse(event.data);
                onCardFieldMessage(data);
                let fdmsMessage = {};
                let formErrors = { ...errors };
                if (data.success === false) {
                    formErrors.cardNumber = 'CC_NumberInvalid';
                }
                else if (data.cardType === 'AMEX' && !isAmexAccepted) {
                    formErrors.cardNumber = 'CC_AmexNotAccepted';
                }
                else if (data.success) {
                    fdmsMessage = data;
                    // Remove cardNumber error from errors object
                    const { cardNumber, ...rest } = formErrors;
                    formErrors = { ...rest };
                }
                setErrors(formErrors);
                onFormError(formErrors);
                setCardData(fdmsMessage);
            }
        };
        window.addEventListener('message', handleMessage, false);
        return () => {
            window.removeEventListener('message', handleMessage, false);
        };
    }, [errors, onCardFieldMessage, isAmexAccepted, onFormError]);
    const { handleFieldChange, handleFieldBlur } = useFieldMapping({
        fieldMapping,
        values,
        errors,
        setErrors,
        onFieldChange,
        onFieldBlur,
        onFormError,
        validationSchema: paymentFormSchema,
        context: cardData,
        defaultValues,
    });
    const fieldsToCheck = useMemo(() => {
        const baseFields = [...PAYMENT_FORM_VALUE_CHECK_FIELDS];
        if (mode !== TxPaymentCardFormMode.TransactionAdd) {
            // Exclude CVV from fields to check in non-TransactionAdd modes
            const index = baseFields.indexOf('cvv');
            if (index > -1) {
                baseFields.splice(index, 1);
            }
        }
        return baseFields;
    }, [mode]);
    usePaymentCardFormFilled({ values, onFormFilled, fieldsToCheck });
    const closeModal = () => {
        setIsCardModalOpen(false);
    };
    const handleIframeError = () => {
        console.log('Error loading iframe');
    };
    const getErrorMessage = (field) => {
        if (errorMessages[field]) {
            return errorMessages[field];
        }
        return lookupErrorMessage(errors[field], spanish);
    };
    if (mode !== TxPaymentCardFormMode.Edit && !iFrameUrl) {
        console.error('iFrameUrl is required when mode is not Edit');
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("form", { autoComplete: "off", className: classNames('tx-payment-card-form', className) },
            React.createElement(TxFormGroup, null,
                React.createElement("p", { className: "tx-payment-card-form__header" }, paymentMethodTitle),
                paymentMethodSubTitle && (React.createElement("p", { className: "tx-payment-card-form__sub_header" },
                    paymentMethodSubTitle,
                    " ",
                    getAcceptedCardsString(acceptedCards, spanish),
                    ".")),
                React.createElement("input", { type: "text", name: "fake-username", autoComplete: "username", style: { display: 'none' }, tabIndex: -1, "aria-hidden": "true" }),
                React.createElement("input", { type: "password", name: "fake-password", autoComplete: "current-password", style: { display: 'none' }, tabIndex: -1, "aria-hidden": "true" }),
                React.createElement(TxInput, { name: "texkitname", id: "texkitname", onChange: handleFieldChange, onBlur: handleFieldBlur, label: labels.nameOnCard, className: "mb-5", errorMessage: getErrorMessage('name'), maxLength: 80, required: true, value: values.name || '', autoComplete: "new-password", role: "textbox", "aria-autocomplete": "none", "data-lpignore": "true", "data-form-type": "other" }),
                mode === TxPaymentCardFormMode.Edit ? (React.createElement(TxInput, { disabled: true, label: labels.cardNumber, className: "mb-5", optionalText: "", placeholder: values.cardNumber ? `**** **** **** ${values.cardNumber}` : '' })) : (React.createElement("div", { className: "tx-payment-card-form__card" },
                    React.createElement("label", { className: "tx-payment-card-form__label" },
                        React.createElement("span", { className: classNames('tx-payment-card-form__label__text') }, labels.cardNumber),
                        React.createElement("div", { className: classNames('tx-payment-card-form__card_number', {
                                'tx-payment-card-form__card_number--error': Boolean(getErrorMessage('cardNumber')),
                            }), style: { position: 'relative' } },
                            React.createElement("iframe", { src: iFrameUrl, onError: handleIframeError, style: {
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                } }))),
                    getErrorMessage('cardNumber') && (React.createElement("div", { className: "tx-payment-card-form__error-message", "data-testid": "error-msg" }, getErrorMessage('cardNumber'))))),
                React.createElement(TxInputMask, { mask: "99/9999", onChange: handleFieldChange, value: values.expirationDate || '' },
                    React.createElement(TxInput, { name: "texkitexpiry", id: "texkitexpiry", onChange: handleFieldChange, onBlur: handleFieldBlur, label: labels.expirationDate, className: "mb-5", placeholder: labels.expirationDatePlaceholder, errorMessage: getErrorMessage('expirationDate'), required: true, autoComplete: "new-password", role: "textbox", "aria-autocomplete": "none", "data-lpignore": "true", "data-form-type": "other" })),
                mode === TxPaymentCardFormMode.TransactionAdd && (React.createElement("div", { className: "tx-payment-card-form-cvv mb-5" },
                    React.createElement(TxInputMask, { mask: "9999", onChange: handleFieldChange, value: values.cvv },
                        React.createElement(TxInput, { name: "cvv", id: "cvv", onChange: handleFieldChange, onBlur: handleFieldBlur, label: labels.securityCode, errorMessage: getErrorMessage('cvv'), optionalText: "", maxLength: 4, minLength: 3, autoComplete: "new-password", role: "textbox", "aria-autocomplete": "none", "data-lpignore": "true", "data-form-type": "other" })),
                    React.createElement("span", { className: "tx-payment-card-form__whats-this", onClick: () => setIsCardModalOpen(true) }, labels.whatsThis))),
                React.createElement("p", { className: "tx-payment-card-form__header" }, billingAddressTitle),
                billingAddressSubTitle && (React.createElement("p", { className: "tx-payment-card-form__sub_header" }, billingAddressSubTitle)),
                React.createElement(TxInput, { name: "texkitaddress", id: "texkitaddress", onChange: handleFieldChange, onBlur: handleFieldBlur, label: labels.streetAddress, required: true, className: "mb-5", errorMessage: getErrorMessage('streetAddress'), maxLength: 40, value: values.streetAddress || '', autoComplete: "new-password", role: "textbox", "aria-autocomplete": "none", "data-lpignore": "true", "data-form-type": "other" }),
                React.createElement(TxInput, { name: "texkitaddress2", id: "texkitaddress2", onChange: handleFieldChange, onBlur: handleFieldBlur, label: labels.streetAddress2, className: "mb-5", errorMessage: getErrorMessage('streetAddress2'), maxLength: 40, optionalText: labels.optional, value: values.streetAddress2 || '', autoComplete: "new-password", role: "textbox", "aria-autocomplete": "none" }),
                React.createElement(TxInput, { name: "texkitcity", id: "texkitcity", onChange: handleFieldChange, onBlur: handleFieldBlur, label: labels.city, required: true, className: "mb-5", errorMessage: getErrorMessage('city'), maxLength: 40, value: values.city || '', autoComplete: "new-password", role: "textbox", "aria-autocomplete": "none" }),
                React.createElement(TxAutocomplete, { items: stateNames },
                    React.createElement(TxInput, { name: "state", id: "state", onChange: handleFieldChange, onBlur: handleFieldBlur, label: labels.state, placeholder: labels.statePlaceholder, required: true, errorMessage: getErrorMessage('state'), maxLength: 100, icon: React.createElement(CaretIcon, null), value: getStateDisplayValue(values.state), autoComplete: "new-password", role: "textbox", "aria-autocomplete": "none" })),
                React.createElement(TxInputMask, { mask: "99999", onChange: handleFieldChange, value: values.zip || '' },
                    React.createElement(TxInput, { name: "texkitzip", id: "texkitzip", onChange: handleFieldChange, onBlur: handleFieldBlur, label: labels.zipCode, required: true, className: "tx-payment-card-form-zip mb-0", errorMessage: getErrorMessage('zip'), maxLength: 5, autoComplete: "new-password", role: "textbox", "aria-autocomplete": "none" })))),
        React.createElement(WhatsThisModal, { open: isCardModalOpen, onClose: closeModal, spanish: spanish, focusOnFirst: false, showAmex: isAmexAccepted })));
};
//# sourceMappingURL=PaymentCardForm.js.map